import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Import your components
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { PrivateRoute } from './components/auth/PrivateRoute';
import { RoleRoute } from './components/auth/RoleRoute';
import { AuthProvider } from './context/AuthContext';
import { RbacProvider } from './context/RbacContext';
import { LoadingProvider } from './context/LoadingContext';
import { Home } from './pages/Home';
import AboutPage from './pages/AboutPage';
import { ServicesPage } from './pages/ServicesPage';
import Products from './pages/Products';
import { FeaturesPage } from './pages/FeaturesPage';
import { Contact } from './pages/Contact';
import { Blog } from './pages/Blog';
import { FAQ } from './pages/FAQ';
import { Portfolio } from './pages/Portfolio';
import { Pricing } from './pages/Pricing';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { PublicLayout } from './components/layout/PublicLayout';
import { CustomerPortal } from './components/customer-portal/CustomerPortal';
import { AdminLayout } from './components/admin/AdminLayout';
import AdminRoutes from './components/admin/AdminRoutes';
import { ManagerLayout } from './components/manager/ManagerLayout';
import ManagerRoutes from './components/manager/ManagerRoutes';
import { WorkerLayout } from './components/worker/WorkerLayout';
import WorkerRoutes from './components/worker/WorkerRoutes';
import { CustomerLayout } from './components/customer-portal/CustomerLayout';
import CustomerRoutes from './components/customer-portal/CustomerRoutes';
import { OrderManagement } from './components/orders/OrderManagement';
import { InventoryManagement } from './components/inventory/InventoryManagement';
import { CustomerManagement } from './components/customers/CustomerManagement';
import { QualityControlManagement } from './components/quality-control/QualityControlManagement';
import { MaintenanceManagement } from './components/maintenance/MaintenanceManagement';
import { ProductionLineManagement } from './components/production/ProductionLineManagement';
import { ProductionQueue } from './components/production/ProductionQueue';
import { SupplyChainManagement } from './components/supply-chain/SupplyChainManagement';
import { ReportGenerator } from './components/reports/ReportGenerator';
import { ProductManagement } from './components/products/ProductManagement';
import { ProductConfiguratorWrapper } from './components/products/ProductConfiguratorWrapper';
import { ProductDisplayWrapper } from './components/products/ProductDisplayWrapper';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AboutStory } from './pages/AboutStory';
import { AboutTeam } from './pages/AboutTeam';
import { ProductDetail } from './pages/ProductDetail';
import { ProjectDetail } from './pages/ProjectDetail';
import { QuoteRequest } from './pages/QuoteRequest';
import { BookTour } from './pages/BookTour';
import { useRbac } from './context/RbacContext';

// Role-based redirect component
const RoleBasedRedirect: React.FC = () => {
  const { hasRole, roles, user } = useRbac();
  
  // Debug logging
  console.log('RoleBasedRedirect - User roles:', roles);
  console.log('RoleBasedRedirect - User role object:', user?.role);
  console.log('RoleBasedRedirect - hasRole admin:', hasRole('admin'));
  console.log('RoleBasedRedirect - hasRole manager:', hasRole('manager'));
  console.log('RoleBasedRedirect - hasRole worker:', hasRole('worker'));
  console.log('RoleBasedRedirect - hasRole customer:', hasRole('customer'));
  
  if (hasRole('admin')) {
    console.log('RoleBasedRedirect - Redirecting to admin dashboard');
    return <Navigate to="/admin/dashboard" replace />;
  } else if (hasRole('manager')) {
    console.log('RoleBasedRedirect - Redirecting to manager dashboard');
    return <Navigate to="/manager/dashboard" replace />;
  } else if (hasRole('worker')) {
    console.log('RoleBasedRedirect - Redirecting to worker dashboard');
    return <Navigate to="/worker/dashboard" replace />;
  } else if (hasRole('customer')) {
    console.log('RoleBasedRedirect - Redirecting to customer portal');
    return <Navigate to="/customer-portal" replace />;
  } else {
    console.log('RoleBasedRedirect - No role detected, defaulting to customer portal');
    return <Navigate to="/customer-portal" replace />;
  }
};

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <LoadingProvider>
        <AuthProvider>
          <RbacProvider>
            <div className="App">
              <Routes>
                {/* Public Routes with PublicLayout */}
                <Route path="/" element={<PublicLayout />}>
                  <Route index element={<Home />} />
                  <Route path="about" element={<AboutPage />} />
                  <Route path="about/story" element={<AboutStory />} />
                  <Route path="about/team" element={<AboutTeam />} />
                  <Route path="services" element={<ServicesPage />} />
                  <Route path="products" element={<Products />} />
                  <Route path="products/display/:productId" element={<ProductDetail />} />
                  <Route path="features" element={<FeaturesPage />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="blog" element={<Blog />} />
                  <Route path="faq" element={<FAQ />} />
                  <Route path="portfolio" element={<Portfolio />} />
                  <Route path="portfolio/:projectId" element={<ProjectDetail />} />
                  <Route path="pricing" element={<Pricing />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="terms-of-service" element={<TermsOfService />} />
                  <Route path="quote" element={<QuoteRequest />} />
                  <Route path="book-tour" element={<BookTour />} />
                </Route>
                
                {/* Auth Routes */}
                <Route path="auth">
                  <Route path="login" element={<Login />} />
                  <Route path="register" element={<Register />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                </Route>

                {/* Protected Routes */}

                {/* Admin Routes with AdminLayout */}
                <Route
                  path="/admin/*"
                  element={
                    <PrivateRoute>
                      <RoleRoute allowedRoles={['admin']}>
                        <AdminLayout>
                          <AdminRoutes />
                        </AdminLayout>
                      </RoleRoute>
                    </PrivateRoute>
                  }
                />

                {/* Manager Routes with ManagerLayout */}
                <Route
                  path="/manager/*"
                  element={
                    <PrivateRoute>
                      <RoleRoute allowedRoles={['manager']}>
                        <ManagerLayout>
                          <ManagerRoutes />
                        </ManagerLayout>
                      </RoleRoute>
                    </PrivateRoute>
                  }
                />

                {/* Worker Routes with WorkerLayout */}
                <Route
                  path="/worker/*"
                  element={
                    <PrivateRoute>
                      <RoleRoute allowedRoles={['worker']}>
                        <WorkerLayout>
                          <WorkerRoutes />
                        </WorkerLayout>
                      </RoleRoute>
                    </PrivateRoute>
                  }
                />

                {/* Customer Routes with CustomerLayout */}
                <Route
                  path="/customer-portal/*"
                  element={
                    <PrivateRoute>
                      <RoleRoute allowedRoles={['customer']}>
                        <CustomerLayout>
                          <CustomerRoutes />
                        </CustomerLayout>
                      </RoleRoute>
                    </PrivateRoute>
                  }
                />

                {/* Legacy routes for backward compatibility - redirect to role-specific routes */}
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <RoleBasedRedirect />
                    </PrivateRoute>
                  }
                />

                {/* Catch all route - redirect to role-specific dashboard for authenticated users, login for others */}
                <Route path="*" element={
                  <PrivateRoute>
                    <RoleBasedRedirect />
                  </PrivateRoute>
                } />
              </Routes>
            </div>
          </RbacProvider>
        </AuthProvider>
      </LoadingProvider>
    </ErrorBoundary>
  );
};

export default App;
